import React, { useCallback, useContext, useEffect, useRef } from "react";
import { usePrevious } from "react-use";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import mapboxgl from "mapbox-gl";

import { MapContext } from "fond/map/MapProvider";
import { length } from "fond/turf";
import { EditMode, SystemOfMeasurement } from "fond/types";
import { toBest } from "fond/utils";

interface BestValue {
  plural: string;
  singular: string;
  unit: string;
  val: number;
}

interface IProps {
  /**
   * The current EditMode of the map.
   */
  editMode: EditMode;
  /**
   * Callback function for when the measured distance changes
   */
  onChange(distance?: string): void;

  /**
   * The ruler's system of measurement.
   */
  systemOfMeasurement?: SystemOfMeasurement;
}

const BaseRuler: React.FC<IProps> = ({ editMode, onChange, systemOfMeasurement }: IProps) => {
  const prevEditMode = usePrevious(editMode);
  const { map, drawControl, setDrawMode } = useContext(MapContext);
  // Track the draw control instance created by the ruler so tearDown
  // can avoid removing a draw control that was replaced by another tool.
  const rulerDrawControlRef = useRef(drawControl.current);

  /**
   * When the user clicks on the map we check if the user
   * is attempting to start another measurement.  If so we
   * remove the old features and enter drawing mode again
   */
  const handleOnMapClick: (event: mapboxgl.MapMouseEvent) => void = useCallback(
    (_event: mapboxgl.MapMouseEvent) => {
      // Check if the user clicking off the drawn measurement
      if (drawControl.current && drawControl.current.getSelected().features.length === 0) {
        map?.off("click", handleOnMapClick);
        drawControl.current.deleteAll().changeMode("draw_line_string", { multiCreate: false });
      }
    },
    [drawControl, map]
  );

  /**
   * When the drawn feature is created we monitor the map
   * for when the user wants to start another measurement
   */
  const handleOnCreate = useCallback(
    (_event: MapboxDraw.DrawCreateEvent) => {
      map?.on("click", handleOnMapClick);
    },
    [map, handleOnMapClick]
  );

  /**
   * Callback for when mapbox draw registers a change in features.
   * When that occurs we determine the new length of the lineString drawn
   */
  const updateDistance = useCallback(
    (_event?: MapboxDraw.DrawEvent) => {
      if (drawControl.current && map?.hasControl(drawControl.current)) {
        const lineString = drawControl.current.getAll?.();
        if (lineString) {
          const value = length(lineString, { units: systemOfMeasurement === "metric" ? "meters" : "feet" });
          const distance: BestValue = toBest(value, {
            from: systemOfMeasurement === "metric" ? "m" : "ft",
          });
          onChange(`${distance.val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${distance.unit}`);
        }
      }
    },
    [drawControl, map, onChange, systemOfMeasurement]
  );

  /**
   * Tear down all the additional map layers, events & source
   */
  const tearDown = useCallback(() => {
    onChange();
    // Only remove the draw control if it hasn't been replaced by another tool
    // (e.g., DrawHandler creates a fresh instance when entering a draw mode).
    // If the control was replaced, the new owner is responsible for cleanup.
    if (!drawControl.current || drawControl.current === rulerDrawControlRef.current) {
      setDrawMode("no_feature");
    }
    rulerDrawControlRef.current = null;
    map?.off("click", handleOnMapClick);
    map?.off("draw.create", handleOnCreate);
    map?.off("draw.render", updateDistance);
    map?.off("draw.delete", updateDistance);
    map?.off("draw.update", updateDistance);
  }, [onChange, drawControl, setDrawMode, map, handleOnMapClick, handleOnCreate, updateDistance]);

  /**
   * Toggles measuring mode
   */
  const startMeasuring = useCallback(() => {
    map?.on("draw.create", handleOnCreate);
    map?.on("draw.render", updateDistance);
    map?.on("draw.delete", updateDistance);
    map?.on("draw.update", updateDistance);

    setDrawMode("draw_line_string", { multiCreate: false });
    // Capture the draw control instance so tearDown knows which instance belongs to the ruler
    rulerDrawControlRef.current = drawControl.current;
  }, [map, handleOnCreate, updateDistance, setDrawMode, drawControl]);

  // Keep a ref to the latest tearDown for the unmount-only effect
  const tearDownRef = useRef(tearDown);
  tearDownRef.current = tearDown;

  useEffect(() => {
    return () => {
      tearDownRef.current();
    };
  }, []);

  // Monitor editMode & if it changes tearDown measuring
  // For example the user starts commenting
  useEffect(() => {
    if (prevEditMode === "measure" && editMode !== "measure") {
      tearDown();
    } else if (prevEditMode !== "measure" && editMode === "measure") {
      startMeasuring();
    }
  }, [editMode, prevEditMode, tearDown, startMeasuring]);

  // Monitor for changes in the System of Measurements
  useEffect(() => {
    if (drawControl) {
      updateDistance();
    }
  }, [systemOfMeasurement, drawControl, updateDistance]);

  return null;
};

export default BaseRuler;
